/**
 * @Description 选址地图青春版-左侧抽屉-关注品牌组件
 * 国省市视角和分公司视角下的各个子视角都公用(小天才除外)
 * 点位测评-详情-城市信息
 * https://confluence.lanhanba.com/pages/viewpage.action?pageId=149544836
 */
import { FC, useMemo, useState } from 'react';
import cs from 'classnames';
import styles from './index.module.less';
import { isNotEmptyAny } from '@lhb/func';
import Title from '@/views/youth/pages/map/components/LeftContent/components/Title';
import LeftConOfTopBrand from '@/common/components/business/YouthMap/LeftConOfTopBrand';
import LeftConOfBrandTendency from '@/common/components/business/YouthMap/LeftConOfBrandTendency';
import V2Title from '@/common/components/Feedback/V2Title';
import V2Empty from '@/common/components/Data/V2Empty';
import CompetitionFilter, { COMPETITION_ALL } from '@/common/components/business/CompetitionFilter';

interface CommonBrandModuleProps {
  brandList: any[]; // 品牌列表数据
  brandData: any[]; // 品牌开店趋势数据
  isCityView?: boolean; // 是否是城市视角
  inSpotevaluation?: boolean; /** 是否在点位测评使用 */
  className?: any;
  isSAAS?: boolean; // 是否是saas
  title?: string; // 自定义标题，不传则使用默认"关注品牌"
  showCompetitionFilter?: boolean; // 是否展示竞合关系筛选
};

const CommonBrandModule: FC<CommonBrandModuleProps> = ({
  brandList,
  brandData,
  isCityView = false,
  inSpotevaluation = false,
  className,
  isSAAS = false,
  title,
  showCompetitionFilter = false,
}) => {
  const [competition, setCompetition] = useState<string | number>(COMPETITION_ALL); // 竞合关系筛选，默认全部
  const displayTitle = title || '关注品牌';
  // 竞合关系为纯前端筛选：全部展示所有数据，其他选项仅保留匹配的竞合关系
  const filteredBrandList = useMemo(() => {
    if (!showCompetitionFilter || competition === COMPETITION_ALL) return brandList;
    return brandList.filter((item) => item.competition === competition);
  }, [brandList, competition, showCompetitionFilter]);
  // 趋势接口不返回竞合关系，用品牌列表的竞合关系补到趋势数据上（仅用于角标展示）
  // 未开启竞协区分时不处理，直接返回原数据，保持历史表现不变
  const enrichedBrandData = useMemo(() => {
    if (!showCompetitionFilter) return brandData;
    const competitionMap = new Map((brandList || []).map((item) => [item.brandId, item]));
    return (brandData || []).map((item) => {
      const brand = competitionMap.get(item.brandId);
      return {
        ...item,
        competition: item.competition ?? brand?.competition,
        competitionName: item.competitionName || brand?.competitionName,
      };
    });
  }, [brandData, brandList, showCompetitionFilter]);
  return (
    <>
      { inSpotevaluation ? <V2Title type='H2' text={displayTitle} className={styles.title}/> : <Title icon={{ iconHref: 'iconic_qushi' }} className='mt-5'>{displayTitle}</Title>
      }
      <div className={cs('pl-16 pr-16', className)}>
        {/* 竞合关系筛选 */}
        {showCompetitionFilter && isNotEmptyAny(brandList) && (
          <CompetitionFilter onChange={setCompetition}/>
        )}
        {/* 关注品牌 */}
        {
          isNotEmptyAny(filteredBrandList) && (
            <LeftConOfTopBrand
              brandList={filteredBrandList}
              isSAAS={isSAAS}
            />
          )
        }
        {/* 筛选后无数据用缺省图占位；未开启竞协区分时（无筛选）不渲染，保持历史表现 */}
        {
          showCompetitionFilter && !isNotEmptyAny(filteredBrandList) && (
            <V2Empty className='mt-12 mb-8'/>
          )
        }
        {/* 品牌门店趋势 */}
        {
          isNotEmptyAny(enrichedBrandData) && (
            <LeftConOfBrandTendency
              brandData={enrichedBrandData}
              isCityView={isCityView}
              showCompetitionBadge={showCompetitionFilter}
            />
          )
        }
      </div>
    </>
  );
};

export default CommonBrandModule;
